"use client";

import ThreeSixtyIcon from "@mui/icons-material/ThreeSixty";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { BodyPartType } from "@rmh/defs";
import { useState } from "react";

import { BodyFigureSvg } from "./BodyFigureSvg";
import {
  BACK_OUTLINE_D,
  BACK_PATHS,
  BACK_VIEWBOX,
  FRONT_OUTLINE_D,
  FRONT_PATHS,
  FRONT_VIEWBOX,
} from "./bodyFigurePaths";
import {
  DEFAULT_ONBOARDING_BODY_FIGURE_LABELS,
  type OnboardingBodyFigureLabels,
} from "./labels";

export type BodyFigureView = "front" | "back";

export interface OnboardingBodyFigureProps {
  selectedPart: BodyPartType | null;
  onSelectedPartChange: (part: BodyPartType | null) => void;
  initialView?: BodyFigureView;
  /** Overrides every visible/aria string. Defaults to English. */
  labels?: OnboardingBodyFigureLabels;
  dataCy?: string;
  className?: string;
}

export const OnboardingBodyFigure = ({
  selectedPart,
  onSelectedPartChange,
  initialView = "front",
  labels = DEFAULT_ONBOARDING_BODY_FIGURE_LABELS,
  dataCy = "onboarding_body_figure",
  className,
}: OnboardingBodyFigureProps) => {
  const [view, setView] = useState<BodyFigureView>(initialView);

  const handlePartClick = (part: BodyPartType) => {
    if (part === selectedPart) {
      onSelectedPartChange(null);
    } else {
      onSelectedPartChange(part);
    }
  };

  const toggleView = () => setView((v) => (v === "front" ? "back" : "front"));

  const isFront = view === "front";
  const viewLabel = isFront ? labels.frontView : labels.backView;
  const rotateAriaLabel = isFront ? labels.rotateToBack : labels.rotateToFront;
  const leftSideLabel = isFront ? labels.right : labels.left;
  const rightSideLabel = isFront ? labels.left : labels.right;

  return (
    <Box
      className={className}
      sx={(theme) => ({
        position: "relative",
        width: "100%",
        aspectRatio: "408 / 574",
        backgroundColor: theme.vars!.palette.rmh.paper["0-base"],
        borderWidth: theme.spacing(0.25),
        borderStyle: "solid",
        borderColor: theme.vars!.palette.rmh.border["1-surface"].rest,
        borderRadius: theme.shape.medium,
        padding: 4,
        boxSizing: "border-box",
      })}
      data-cy={dataCy}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={(theme) => ({
          position: "absolute",
          top: theme.spacing(4),
          left: theme.spacing(4),
          right: theme.spacing(4),
          zIndex: 1,
          alignItems: "flex-start",
        })}
      >
        <Typography
          component="span"
          variant="h6"
          sx={(theme) => ({
            color: theme.vars!.palette.rmh.text.brand,
          })}
          data-cy={`${dataCy}__view_label`}
        >
          {viewLabel}
        </Typography>
        <Button
          type="button"
          variant="outlined"
          size="xsmall"
          onClick={toggleView}
          endIcon={
            <ThreeSixtyIcon
              sx={(theme) => ({ color: theme.vars!.palette.rmh.icon.brand })}
            />
          }
          aria-label={rotateAriaLabel}
          data-cy={`${dataCy}__rotate`}
          sx={(theme) => ({
            backgroundColor: theme.vars!.palette.rmh.paper["1-surface"].rest,
            textTransform: "none",
          })}
        >
          {labels.rotate}
        </Button>
      </Stack>

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
          boxSizing: "border-box",
        }}
      >
        <BodyFigureSvg
          viewBox={isFront ? FRONT_VIEWBOX : BACK_VIEWBOX}
          paths={isFront ? FRONT_PATHS : BACK_PATHS}
          outlineD={isFront ? FRONT_OUTLINE_D : BACK_OUTLINE_D}
          selectedPart={selectedPart}
          onPartClick={handlePartClick}
          ariaLabel={viewLabel}
          partLabels={labels.parts}
          dataCyPrefix={`${dataCy}__${view}`}
        />
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={(theme) => ({
          position: "absolute",
          bottom: theme.spacing(8),
          left: "20%",
          right: "20%",
          pointerEvents: "none",
        })}
      >
        <Typography
          component="span"
          variant="buttonSm"
          sx={(theme) => ({
            color: theme.vars!.palette.rmh.text.secondary,
          })}
        >
          {leftSideLabel}
        </Typography>
        <Typography
          component="span"
          variant="buttonSm"
          sx={(theme) => ({
            color: theme.vars!.palette.rmh.text.secondary,
          })}
        >
          {rightSideLabel}
        </Typography>
      </Stack>
    </Box>
  );
};
